/**
 * One polling hook for every view that reads a snapshot endpoint.
 *
 * Written once because the rule it encodes is easy to get wrong in each copy: a failed refresh
 * does NOT discard the last good data. It marks it stale and says why. Blanking the screen on
 * one bad poll makes a hiccup look like an outage, and a view that empties itself is
 * indistinguishable from a view reporting that nothing is there.
 */
import { useEffect, useState } from 'react';
import type { ApiFailure, ApiResult } from './api';

export interface PollState<T> {
  data: T | null;
  failure: ApiFailure | null;
  loading: boolean;
}

export function usePoll<T>(fetcher: () => Promise<ApiResult<T>>, intervalMs = 20_000): PollState<T> {
  const [state, setState] = useState<PollState<T>>({ data: null, failure: null, loading: true });

  useEffect(() => {
    let alive = true;
    async function tick() {
      const r = await fetcher();
      if (!alive) return;
      setState((prev) => r.ok
        ? { data: r.data, failure: null, loading: false }
        : { data: prev.data, failure: r.failure, loading: false });
    }
    void tick();
    const t = setInterval(() => void tick(), intervalMs);
    return () => { alive = false; clearInterval(t); };
    // The fetcher is a module-level constant in every call site.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intervalMs]);

  return state;
}
